import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bootstrapSession } from "auth";
import { Button, Notification, Spinner, toast } from "components/ui";
import { ImSpinner9 } from "react-icons/im";
import AccountService from "views/v2/services/accountService";

const INTEGRATION_SETTINGS_PATH = "/adminsettings/faxIntegration";
const SIGN_IN_PATH = "/sign-in";

/**
 * RingCentral OAuth lands here in a popup outside AppProvider/AuthProvider.
 * Prefer handing the code to the opener (same as Gmail/O365) so the parent
 * window — which already has a hydrated authStore — attaches
 * Authorization: Bearer <EZROI JWT>.
 */
const RingCentralCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasSubmittedRef = useRef(false);

  const [status, setStatus] = useState("connecting");
  const [message, setMessage] = useState("Connecting RingCentral...");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canRetry, setCanRetry] = useState(false);
  const [requiresSignIn, setRequiresSignIn] = useState(false);

  const code = searchParams.get("code");
  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  const redirectToSettings = useCallback(() => {
    navigate(INTEGRATION_SETTINGS_PATH, { replace: true });
  }, [navigate]);

  const notifyOpenerAndClose = useCallback(() => {
    if (!window.opener || window.opener.closed) {
      return false;
    }

    const payload = {};
    for (const [key, value] of searchParams.entries()) {
      payload[key] = value;
    }

    window.opener.postMessage({ type: "RINGCENTRAL", payload }, "*");
    window.close();
    return true;
  }, [searchParams]);

  const handleExchangeCode = useCallback(async () => {
    if (notifyOpenerAndClose()) {
      setMessage("Finishing RingCentral connection...");
      return;
    }

    if (isSubmitting) {
      return;
    }

    if (error) {
      const callbackErrorMessage = errorDescription || error;
      setStatus("failed");
      setRequiresSignIn(false);
      setMessage(callbackErrorMessage);
      toast.push(
        <Notification title="RingCentral connection failed" type="danger" closable>
          {callbackErrorMessage}
        </Notification>
      );
      return;
    }

    if (!code) {
      setStatus("failed");
      setRequiresSignIn(false);
      setMessage("Invalid callback. Missing authorization code.");
      return;
    }

    setCanRetry(false);
    setRequiresSignIn(false);
    setIsSubmitting(true);
    setStatus("connecting");
    setMessage("Connecting RingCentral...");

    try {
      // No opener (full-page redirect): hydrate in-memory authStore first.
      // Axios auth interceptors only send Bearer when authStore has a token.
      await bootstrapSession();

      await AccountService.postIntegrationOAuthByProvider("RINGCENTRAL", {
        code,
      });

      setStatus("success");
      setMessage("RingCentral connected");
      toast.push(
        <Notification title="Success" type="success" closable>
          RingCentral connected
        </Notification>
      );
      setTimeout(redirectToSettings, 1200);
    } catch (requestError) {
      const statusCode = requestError?.response?.status;
      const backendMessage =
        requestError?.response?.data?.message ||
        requestError?.response?.data?.error?.message;

      if (statusCode === 401 || statusCode === 403) {
        setStatus("failed");
        setRequiresSignIn(true);
        setMessage("Your session expired. Please sign in again.");
        return;
      }

      if (statusCode === 400) {
        setStatus("failed");
        setRequiresSignIn(false);
        setMessage(backendMessage || "Invalid callback data.");
        return;
      }

      if (!requestError?.response) {
        setStatus("failed");
        setRequiresSignIn(false);
        setMessage("Network error while connecting RingCentral.");
        setCanRetry(true);
        return;
      }

      setStatus("failed");
      setRequiresSignIn(false);
      setMessage(
        backendMessage || "Failed to connect RingCentral. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    code,
    error,
    errorDescription,
    isSubmitting,
    notifyOpenerAndClose,
    redirectToSettings,
  ]);

  useEffect(() => {
    if (hasSubmittedRef.current) {
      return;
    }
    hasSubmittedRef.current = true;
    handleExchangeCode();
  }, [handleExchangeCode]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center bg-white dark:bg-[#161616]">
        <p className="text-lg font-semibold mb-3">
          {status === "connecting"
            ? "Connecting"
            : status === "success"
              ? "Success"
              : "Failed"}
        </p>

        {status === "connecting" && (
          <div className="flex justify-center mb-4">
            <Spinner size={34} indicator={ImSpinner9} />
          </div>
        )}

        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{message}</p>

        {status === "success" && (
          <Button variant="solid" onClick={redirectToSettings}>
            Go to integrations
          </Button>
        )}

        {status === "failed" && (
          <div className="flex items-center justify-center gap-2">
            {canRetry && (
              <Button
                variant="solid"
                onClick={handleExchangeCode}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Retry
              </Button>
            )}

            {(searchParams.get("error") || !code || canRetry) && (
              <Button variant="twoTone" onClick={redirectToSettings}>
                Back to integrations
              </Button>
            )}

            {!canRetry && requiresSignIn && (
              <Button variant="solid" onClick={() => navigate(SIGN_IN_PATH)}>
                Sign in
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RingCentralCallbackPage;
